export interface IIframeContentSize {
  width: number;
  height: number;
}

const OVERFLOW_EPSILON_PX = 2;
const MAX_OVERFLOW_NODES = 1500;
const IGNORE_HEIGHT_INCREASE_PX = 120;

export const isResizeObserverLoopError = (message?: string): boolean => (
  Boolean(message && message.includes('ResizeObserver loop'))
);

export const suppressResizeObserverLoopError = (event: ErrorEvent): void => {
  if (!isResizeObserverLoopError(event.message)) {
    return;
  }
  event.stopImmediatePropagation();
  event.preventDefault();
};

export const getVerticalOverflow = (element: HTMLElement): number => {
  const extra = (element.scrollHeight || 0) - (element.clientHeight || 0);
  return extra > OVERFLOW_EPSILON_PX ? extra : 0;
};

export const getMaxVerticalOverflow = (
  root: HTMLElement,
  maxNodes: number = MAX_OVERFLOW_NODES,
): number => {
  let extra = getVerticalOverflow(root);
  const stack: Element[] = Array.from(root.children);
  let visited = 0;
  while (stack.length && visited < maxNodes) {
    const current = stack.pop();
    visited += 1;
    if (!(current instanceof HTMLElement)) {
      continue;
    }
    extra = Math.max(extra, getVerticalOverflow(current));
    const { children } = current;
    for (let i = 0; i < children.length; i += 1) {
      stack.push(children[i]);
    }
  }
  return extra;
};

export const measureIframeContentSize = (
  doc: Document,
): IIframeContentSize | undefined => {
  const html = doc.documentElement;
  const body = doc.body;
  if (!html || !body) {
    return undefined;
  }
  const width = Math.max(html.scrollWidth || 0, body.scrollWidth || 0);
  // 子页通常是 height:100% + 内部滚动，html/body.scrollHeight 只等于 iframe 视口。
  // 真实内容高度 = 视口布局高度 + 内部滚动容器未露出的溢出。
  const layoutHeight = Math.max(
    html.scrollHeight || 0,
    body.scrollHeight || 0,
    html.offsetHeight || 0,
    body.offsetHeight || 0,
  );
  const height = layoutHeight + getMaxVerticalOverflow(body);
  if (width <= 0 || height <= 0) {
    return undefined;
  }
  return { width, height };
};

export const resolveNextContentSize = (
  current: IIframeContentSize | undefined,
  nextSize: IIframeContentSize,
): IIframeContentSize => {
  if (current?.width === nextSize.width && current.height === nextSize.height) {
    return current;
  }
  // 写回高度后子页 100vh 可能再高出一截页眉，忽略小幅增高避免循环撑开
  if (
    current
    && nextSize.height > current.height
    && nextSize.height - current.height < IGNORE_HEIGHT_INCREASE_PX
  ) {
    return current;
  }
  return nextSize;
};
